import { useState } from 'react';
import { Card, Col, Container, Row, Button, ToggleButtonGroup, ToggleButton, Badge } from 'react-bootstrap';
import { FiCreditCard, FiCheck, FiStar, FiUsers, FiZap, FiArrowRight } from 'react-icons/fi';
import { EnterprisePlan, SubscriptionPlans } from '../utils/plans';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ScrollAnimationWrapper from '../components/Other/ScrollAnimationWrapper';

const PricingPage: React.FC = () => {
    const [period, setPeriod] = useState<"Monthly" | "Yearly">("Monthly");
    const navigate = useNavigate();
    
    return (
        <div className="bg-dark text-white min-vh-100">
            <Container className="py-5" style={{ maxWidth: "1100px" }}>
                <Helmet>
                    <title>Pricing - QuickyChat AI Agents</title>
                    <meta name="description" content="Choose the perfect plan for your AI agent needs. Build intelligent agents with custom knowledge bases, API integrations, and smart functions."/>
                    <meta name="keywords" content="AI agents, pricing, subscription, knowledge base, API integration, QuickyChat"/>
                </Helmet>
                
                <ScrollAnimationWrapper>
                    <div className="text-center mb-5">
                        <h1 className="text-white fw-bold mb-3">
                            <FiCreditCard className="me-3" size={32} />
                            Choose Your AI Agent Plan
                        </h1>
                        <p className="text-light lead mb-4">
                            Build intelligent AI agents with custom knowledge bases and API integrations
                        </p>
                        
                        <Card className="bg-dark border-secondary d-inline-block">
                            <Card.Body className="p-2">
                                <ToggleButtonGroup
                                    type="radio"
                                    name="billing-period"
                                    value={period === "Monthly" ? "monthly" : "yearly"}
                                    className="d-flex"
                                >
                                    <ToggleButton
                                        id="monthly"
                                        variant={period === "Monthly" ? "primary" : "outline-secondary"}
                                        onClick={() => setPeriod("Monthly")}
                                        value="monthly"
                                        className="px-4 py-2 fw-semibold"
                                    >
                                        Monthly
                                    </ToggleButton>
                                    <ToggleButton
                                        id="yearly"
                                        variant={period === "Yearly" ? "primary" : "outline-secondary"}
                                        onClick={() => setPeriod("Yearly")}
                                        value="yearly"
                                        className="px-4 py-2 fw-semibold position-relative"
                                    >
                                        Yearly
                                        <Badge bg="success" className="position-absolute top-0 start-100 translate-middle px-2 py-1 small">
                                            Save 20%
                                        </Badge>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Card.Body>
                        </Card>
                    </div>
                </ScrollAnimationWrapper>

                <Row className="g-4 mb-5">
                    {SubscriptionPlans.map((plan, index) => (
                        <Col key={index} lg={3} md={6}>
                            <ScrollAnimationWrapper>
                                <Card className={`h-100 position-relative ${plan.name === "Pro" 
                                    ? "bg-primary border-primary text-white" 
                                    : "bg-dark border-secondary"}`}
                                    style={plan.name === "Pro" ? {transform: 'scale(1.05)'} : {}}
                                >
                                    {plan.name === "Pro" && (
                                        <div className="position-absolute top-0 start-50 translate-middle">
                                            <Badge bg="warning" text="dark" className="px-3 py-1 fw-bold">
                                                <FiStar className="me-1" size={12} />
                                                MOST POPULAR
                                            </Badge>
                                        </div>
                                    )}
                                    
                                    <Card.Header className={`text-center border-0 ${plan.name === "Pro" ? "bg-primary" : "bg-dark"}`}>
                                        <h4 className={`fw-bold mb-2 ${plan.name === "Pro" ? "text-white" : "text-white"}`}>
                                            {plan.name}
                                        </h4>
                                        <div className="mb-3">
                                            <span className={`display-4 fw-bold ${plan.name === "Pro" ? "text-white" : "text-white"}`}>
                                                ${period === "Monthly" ? plan.monthly.price : plan.yearly.price}
                                            </span>
                                            <span className={`fs-6 text-white-50`}>
                                                /{period === "Monthly" ? "month" : "year"}
                                            </span>
                                        </div>
                                        {plan.discount && (
                                            <p className={`small mb-0 text-white-50`}>
                                                {plan.discount}
                                            </p>
                                        )}
                                    </Card.Header>
                                    
                                    <Card.Body className={`d-flex flex-column ${plan.name === "Pro" ? "bg-primary" : "bg-dark"}`}>
                                        <ul className="list-unstyled mb-4 flex-grow-1">
                                            {plan.features.map((feature, i) => (
                                                <li key={i} className={`mb-2 d-flex align-items-start ${plan.name === "Pro" ? "text-white" : "text-light"}`}>
                                                    <FiCheck className={`me-2 mt-1 flex-shrink-0 ${plan.name === "Pro" ? "text-white" : "text-success"}`} size={16} />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                        
                                        <Button 
                                            onClick={() => navigate('/dashboard')}
                                            variant={plan.name === "Pro" ? "light" : "primary"}
                                            size="lg"
                                            className="w-100 fw-semibold"
                                        >
                                            <FiZap className="me-2" size={16} />
                                            {plan.buttonText}
                                            <FiArrowRight className="ms-2" size={16} />
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </ScrollAnimationWrapper>
                        </Col>
                    ))}
                </Row>
                
                {/* Enterprise Plan */}
                <ScrollAnimationWrapper>
                    <Card className="bg-dark border-warning">
                        <Card.Body className="p-5">
                            <Row className="align-items-center">
                                <Col lg={4} className="mb-4 mb-lg-0">
                                    <div className="mb-4">
                                        <h2 className="text-white fw-bold display-6 mb-2">Enterprise</h2>
                                        <h3 className="text-white fw-bold mb-3">Custom Pricing</h3>
                                        <p className="text-light mb-4">
                                            Flexible pricing based on your specific requirements and usage volume
                                        </p>
                                        <Button
                                            variant="warning"
                                            size="lg"
                                            className="px-5 py-3 fw-bold text-dark"
                                            onClick={() => navigate("/dashboard/support")}
                                        >
                                            <FiUsers className="me-2" size={18} />
                                            Contact Sales Team
                                        </Button>
                                    </div>
                                </Col>
                                
                                <Col lg={4} className="mb-4 mb-lg-0">
                                    <h5 className="text-white fw-bold mb-3">
                                        <FiZap className="me-2 text-warning" size={20} />
                                        Advanced Features
                                    </h5>
                                    <ul className="list-unstyled">
                                        {EnterprisePlan.additionalFeatures?.map((feature, i) => (
                                            <li key={i} className="mb-2 d-flex align-items-start text-light">
                                                <FiCheck className="me-2 mt-1 flex-shrink-0 text-success" size={16} />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                                
                                <Col lg={4}>
                                    <h5 className="text-white fw-bold mb-3">
                                        <FiUsers className="me-2 text-warning" size={20} />
                                        Core Features
                                    </h5>
                                    <ul className="list-unstyled">
                                        {EnterprisePlan.features.map((feature, i) => (
                                            <li key={i} className="mb-2 d-flex align-items-start text-light">
                                                <FiCheck className="me-2 mt-1 flex-shrink-0 text-success" size={16} />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </ScrollAnimationWrapper>
                
                {/* Call to Action */}
                <ScrollAnimationWrapper>
                    <div className="text-center py-5">
                        <h3 className="text-white fw-bold mb-3">Ready to Build Your AI Agent?</h3>
                        <p className="text-light lead mb-4">
                            Start creating intelligent AI agents with custom knowledge bases and API integrations today
                        </p>
                        <Button 
                            variant="primary" 
                            size="lg" 
                            className="px-5 py-3 fw-bold"
                            onClick={() => navigate('/dashboard')}
                        >
                            <FiZap className="me-2" size={18} />
                            Get Started for Free
                            <FiArrowRight className="ms-2" size={16} />
                        </Button>
                    </div>
                </ScrollAnimationWrapper>
            </Container>
        </div>
    );
};

export default PricingPage;